import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  Database,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Button from '../common/design-system/Button';
import { getCollabReport, generateCollabReport, downloadCollabReport } from '../../services/api';
import styles from './CollaborationReport.module.scss';
import MetricInfoPopover from '../common/MetricInfoPopover';

const unavailable = '—';
const hasValue = (value) => value !== null && value !== undefined;
const fmt = (value) => hasValue(value) ? Number(value).toLocaleString() : unavailable;
const money = (value) => hasValue(value)
  ? `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : unavailable;
const pct = (value) => hasValue(value) ? `${Number(value).toFixed(2)}%` : unavailable;
const compactCount = (value) => hasValue(value) ? Number(value).toLocaleString() : unavailable;

const getInitials = (name = '') => {
  const clean = String(name || '').replace('@', '').trim();
  if (!clean) return '?';
  return clean.slice(0, 2).toUpperCase();
};

const Avatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false);
  const label = name || 'Creator';

  return (
    <span className={styles.avatar} aria-label={label}>
      {src && !failed ? (
        <img src={src} alt={label} onError={() => setFailed(true)} />
      ) : (
        <span>{getInitials(label)}</span>
      )}
    </span>
  );
};

const CreatorIdentity = ({ row, compact = false }) => {
  const name = row.creator_name || row.username || row.name || 'Creator';
  const username = row.username ? `@${row.username}` : name;

  return (
    <div className={compact ? styles.creatorCompact : styles.creatorIdentity}>
      <Avatar src={row.profile_picture_url} name={name} />
      <div>
        {!compact && <strong>{name}</strong>}
        <span>{username}</span>
      </div>
    </div>
  );
};

const InfoLabel = ({ children, explanation, label }) => (
  <span className={styles.infoLabel}>
    {children}
    {explanation && <MetricInfoPopover {...explanation} hoverable label={label || `Explain ${children}`} />}
  </span>
);

const MetricCard = ({ icon: Icon, label, value, detail, explanation, featured = false }) => {
  const { t } = useTranslation('collab');
  const infoLabel = t('report.accessibility.metric_info', {
    defaultValue: 'Explain {{metric}}',
    metric: label,
  });

  return (
    <article className={featured ? `${styles.metricCard} ${styles.featuredMetric}` : styles.metricCard}>
      {Icon && <div className={styles.metricIcon}><Icon size={18} aria-hidden="true" /></div>}
      <div className={styles.metricLabel}>
        <span>{label}</span>
        {explanation && <MetricInfoPopover {...explanation} hoverable label={infoLabel} />}
      </div>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </article>
  );
};

const rateCardLabels = {
  reach_cpm: 'Reach CPM',
  impressions_cpm: 'Impression CPM',
  video_views_cpm: 'Video view CPM',
  like_value: 'Value per like',
  save_value: 'Value per save',
  click_value: 'Value per click',
  share_value: 'Value per share',
  comment_value: 'Value per comment',
  profile_visit_value: 'Value per profile visit',
};

const ReportSectionHeading = ({ kicker, title, caption, count }) => (
  <div className={styles.sectionTitleRow}>
    <div>
      <div className={styles.sectionKicker}>{kicker}</div>
      <h2>{title}</h2>
      {caption && <p className={styles.sectionCaption}>{caption}</p>}
    </div>
    {count && <span className={styles.micro}>{count}</span>}
  </div>
);

const ReportRedesign = ({
  report,
  rt,
  dataStatus,
  freshness,
  reportTimestamp,
  isFinalizedSnapshot,
  dashboard,
  creatorSummary,
  contentLog,
  charts,
  metricHelp,
  refreshing,
  downloading,
  handleGenerate,
  handleDownload,
  onBack,
}) => {
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const totalEmv = dashboard.total_emv;
  const realizedInvestment = dashboard.realized_declared_investment;
  const plannedInvestment = dashboard.planned_declared_investment;
  const investmentProgress = hasValue(plannedInvestment) && Number(plannedInvestment) > 0
    ? Math.min(100, Math.round((Number(realizedInvestment || 0) / Number(plannedInvestment)) * 100))
    : 0;
  const totalReach = dashboard.total_reach;
  const totalImpressions = dashboard.total_impressions;
  const totalViews = dashboard.total_video_views;
  const totalEngagements = dashboard.total_engagements;
  const totalCreators = dashboard.total_creators ?? creatorSummary.length;
  const totalAssets = dashboard.total_assets ?? contentLog.length;
  const topCreator = dashboard.top_creator_by_emv || creatorSummary[0];
  const topEngagement = creatorSummary.reduce((best, row) => (
    !best || Number(row.average_engagement_rate || 0) > Number(best.average_engagement_rate || 0) ? row : best
  ), null);
  const composition = charts?.emv_composition || [];
  const baseValue = composition.find((row) => /base|media/i.test(row.name))?.value ?? dashboard.total_base_media_value;
  const engagementValue = composition.find((row) => /engagement/i.test(row.name))?.value ?? dashboard.total_engagement_value;
  const compositionTotal = Number(baseValue || 0) + Number(engagementValue || 0) || 1;
  const metaItems = [
    report.collaboration?.business_name,
    `${report.collaboration?.from_date || rt('meta.no_start', 'No start')} – ${report.collaboration?.to_date || rt('meta.no_end', 'No end')}`,
    report.report?.generated_at ? rt('meta.generated_at', 'Generated {{date}}', { date: new Date(report.report.generated_at).toLocaleString() }) : null,
  ].filter(Boolean);
  const statusText = isFinalizedSnapshot ? 'Final report' : dataStatus?.mode === 'snapshot' ? 'Historical report' : 'Live report';
  const calculatedText = reportTimestamp
    ? new Date(reportTimestamp).toLocaleString()
    : rt('data.no_update', 'No update recorded');
  const freshCount = freshness?.fresh || 0;
  const pendingCount = freshness?.pending || 0;
  const historicCount = freshness?.frozen || 0;
  const creatorContentLabel = (row) => {
    const pieces = contentLog.filter((item) => item.creator_id === row.creator_id);
    if (pieces.length === 1) return pieces[0].content_type || 'Content';
    return `${pieces.length} pieces`;
  };
  const tableHeader = (key, fallback) => rt(`tables.${key}`, fallback);

  return (
    <div className={styles.page}>
      <button className={styles.backButton} onClick={onBack} type="button">
        <ArrowLeft size={16} />
        {rt('actions.back', 'Back to collaboration')}
      </button>

      <header className={styles.hero}>
        <div className={styles.eyebrowRow}>
          <p className={styles.eyebrow}><Sparkles size={14} aria-hidden="true" /> {rt('hero.eyebrow', 'Creator EMV Report')}</p>
          <span className={styles.status}><span className={styles.statusDot} /> {statusText}</span>
        </div>

        <div className={styles.heroGrid}>
          <div className={styles.heroCopy}>
            <h1>{report.collaboration.title}</h1>
            <div className={styles.metaChips} aria-label="Collaboration metadata">
              {metaItems.map((item, index) => <span className={index === 0 ? styles.ownerChip : ''} key={item}>{item}</span>)}
            </div>
          </div>

          <div className={styles.actions}>
            <Button className={styles.reportButton} variant="outline" onClick={handleGenerate} disabled={refreshing}>
              <RefreshCw size={15} className={refreshing ? styles.spin : ''} />
              {refreshing
                ? rt('actions.refreshing', 'Generating…')
                : isFinalizedSnapshot
                  ? rt('actions.new_snapshot', 'Generate new version')
                  : rt('actions.refresh', 'Refresh metrics')}
            </Button>
            <Button className={styles.reportButton} variant="outline" onClick={() => handleDownload('pdf')} disabled={Boolean(downloading)}>
              <Download size={15} /> PDF
            </Button>
            <Button className={`${styles.reportButton} ${styles.reportButtonPrimary}`} onClick={() => handleDownload('xlsx')} disabled={Boolean(downloading)}>
              <FileSpreadsheet size={15} /> Excel
            </Button>
          </div>
        </div>

        <div className={styles.reportToolbar}>
          <div className={styles.dataMode}>
            <span className={styles.snapshotIcon}><Database size={14} /></span>
            <div>
              <strong>{dataStatus?.mode === 'snapshot' ? rt('data.snapshot', 'Saved snapshot') : rt('data.live', 'Live data')}</strong>
              <small>{dataStatus?.analytics_state
                ? rt(`data.analytics_state.${dataStatus.analytics_state}`, dataStatus.analytics_state)
                : rt('data.no_update', 'No analytics state recorded')}</small>
              <small>{dataStatus?.mode === 'snapshot' ? 'Calculated' : 'Updated'} {calculatedText}</small>
            </div>
          </div>
          <div className={styles.reportCounts}>
            <span>{compactCount(totalCreators)} {rt('tables.creators_count', 'creators').replace('{{count}}', '').trim()}</span>
            <span>{compactCount(totalAssets)} {rt('tables.assets_count', 'pieces').replace('{{count}}', '').trim()}</span>
          </div>
        </div>
      </header>

      {(dataStatus?.refresh_temporarily_blocked || freshness) && (
        <section className={styles.notice}>
          <div className={styles.noticeCopy}>
            <span className={styles.noticeIcon}><ShieldCheck size={15} /></span>
            <div>
              <strong>{dataStatus?.refresh_temporarily_blocked
                ? rt('notice.blocked_title', 'Refresh temporarily managed')
                : rt('notice.snapshot_title', 'Data protected by snapshots')}</strong>
              <p>{dataStatus?.refresh_temporarily_blocked
                ? rt('notice.blocked_body', 'We are using the latest saved snapshot. You can refresh again in a few minutes.')
                : rt('notice.snapshot_body', 'This report respects Instagram/Meta limits. Some stories or older pieces may remain historical.')}</p>
            </div>
          </div>
          {freshness && (
            <div className={styles.freshnessPills} aria-label="Snapshot data states">
              <span><strong>{freshCount}</strong> fresh</span>
              <span><strong>{pendingCount}</strong> pending</span>
              <span className={styles.historicPill}><strong>{historicCount}</strong> historic</span>
            </div>
          )}
        </section>
      )}

      <section className={styles.section}>
        <ReportSectionHeading
          kicker="At a glance"
          title="Campaign overview"
          caption="The four metrics a brand should understand first: earned value, actual spend, efficiency and engagement quality."
          count={reportTimestamp ? `Updated ${new Date(reportTimestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : null}
        />
        <div className={`${styles.metricsGrid} ${styles.overviewMetrics}`}>
          <MetricCard icon={null} label="Earned Media Value" value={money(totalEmv)} detail="Total estimated media value generated by the collaboration." explanation={metricHelp.emvTotal} featured />
          <MetricCard icon={null} label="Realized Investment" value={money(realizedInvestment)} detail={`${money(realizedInvestment)} of ${money(plannedInvestment)} planned · ${investmentProgress}% utilized`} explanation={metricHelp.realizedInvestment} />
          <MetricCard icon={null} label="EMV / Investment" value={hasValue(dashboard.estimated_media_return) ? `${dashboard.estimated_media_return}x` : unavailable} detail="Estimated media value generated per $1 invested." explanation={metricHelp.estimatedMediaReturn} />
          <MetricCard icon={null} label="Engagement Rate" value={pct(dashboard.average_engagement_rate)} detail={`${compactCount(totalEngagements)} engagements across ${compactCount(totalReach)} total reach.`} explanation={metricHelp.engagementRate} />
        </div>
        <div className={styles.progress}><span style={{ width: `${investmentProgress}%` }} /></div>
      </section>

      <section className={`${styles.section} ${styles.sectionCard}`}>
        <ReportSectionHeading kicker="Performance" title="Campaign performance" caption="A compact view of how far the content traveled and how audiences interacted with it." />
        <div className={styles.performanceGrid}>
          {[
            ['Reach', totalReach, metricHelp.reach],
            ['Impressions', totalImpressions, metricHelp.impressions],
            ['Views', totalViews, metricHelp.views],
            ['Engagements', totalEngagements, metricHelp.engagements],
            ['Delivered', `${compactCount(totalAssets)} / ${compactCount(totalAssets)}`, metricHelp.assets],
          ].map(([label, value, explanation]) => (
            <div className={styles.metric} key={label}>
              <InfoLabel explanation={explanation}>{label}</InfoLabel>
              <strong>{typeof value === 'number' ? compactCount(value) : value}</strong>
            </div>
          ))}
        </div>
        <div className={styles.funnel}>
          <strong>{compactCount(totalReach)} Reach</strong><span>→</span>
          <strong>{compactCount(totalImpressions)} Impressions</strong><span>→</span>
          <strong>{compactCount(totalViews)} Views</strong><span>→</span>
          <strong>{compactCount(totalEngagements)} Engagements</strong>
        </div>
      </section>

      <section className={`${styles.section} ${styles.sectionCard}`}>
        <ReportSectionHeading kicker="Value model" title="Earned Media Value" caption="A clearer explanation of where the estimated value comes from instead of presenting disconnected figures." />
        <div className={styles.emvLayout}>
          <div className={styles.emvTotal}>
            <div>
              <InfoLabel explanation={metricHelp.emvTotal}>Total estimated media value</InfoLabel>
              <strong>{money(totalEmv)}</strong>
            </div>
            <p>FOTH combines estimated media exposure value with engagement value to create a single campaign valuation metric.</p>
          </div>
          <div className={styles.emvBreakdown}>
            <InfoLabel explanation={metricHelp.emvTotal}>EMV composition</InfoLabel>
            <div className={styles.stackbar} aria-label="EMV composition">
              <span style={{ width: `${(Number(baseValue || 0) / compositionTotal) * 100}%` }} />
              <span style={{ width: `${(Number(engagementValue || 0) / compositionTotal) * 100}%` }} />
            </div>
            {[["Media exposure value", baseValue], ["Engagement value", engagementValue]].map(([label, value], index) => (
              <div className={styles.breakdownRow} key={label}>
                <div className={styles.legend}><i className={index === 0 ? styles.baseDot : styles.engagementDot} /><div><strong>{label}</strong><small>{((Number(value || 0) / compositionTotal) * 100).toFixed(1)}% of EMV</small></div></div>
                <strong>{money(value)}</strong>
              </div>
            ))}
            <div className={styles.equation}><strong>{money(baseValue)}</strong> media exposure + <strong>{money(engagementValue)}</strong> engagement value = <strong>{money(totalEmv)} EMV</strong></div>
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.sectionCard}`}>
        <ReportSectionHeading kicker="Creators" title="Creator performance" caption="Rankings and creator-level performance consolidated into one decision-friendly section." count={`${compactCount(totalCreators)} creators`} />
        <div className={styles.insightGrid}>
          <article className={styles.insightCard}><span>Highest EMV</span><strong>{topCreator ? `${topCreator.creator_name || topCreator.display_name || 'Creator'} · ${money(topCreator.total_emv)}` : '—'}</strong><small>{topCreator?.username ? `@${topCreator.username}` : '—'}</small></article>
          <article className={styles.insightCard}><span>Highest engagement rate</span><strong>{topEngagement ? `${topEngagement.creator_name || topEngagement.display_name || 'Creator'} · ${pct(topEngagement.average_engagement_rate)}` : '—'}</strong><small>{topEngagement?.username ? `@${topEngagement.username}` : '—'}</small></article>
        </div>
        <div className={styles.tableWrap}>
          <table aria-label="Creator performance table">
            <thead><tr>{['creator', 'type', 'reach', 'impressions', 'views', 'engagements', 'er', 'media_base', 'engagement_value', 'emv_total'].map((key) => <th key={key}>{tableHeader(key, key.replaceAll('_', ' '))}</th>)}</tr></thead>
            <tbody>{creatorSummary.map((row) => (
              <tr key={row.creator_id}>
                <td data-label={tableHeader('creator', 'Creator')}><CreatorIdentity row={row} /></td>
                <td data-label={tableHeader('type', 'Content')}>{creatorContentLabel(row)}</td>
                <td data-label={tableHeader('reach', 'Reach')}>{fmt(row.total_reach)}</td>
                <td data-label={tableHeader('impressions', 'Impressions')}>{fmt(row.total_impressions)}</td>
                <td data-label={tableHeader('views', 'Views')}>{fmt(row.total_views)}</td>
                <td data-label={tableHeader('engagements', 'Engagements')}>{fmt(row.total_engagements)}</td>
                <td data-label={tableHeader('er', 'ER')}>{pct(row.average_engagement_rate)}</td>
                <td data-label={tableHeader('media_base', 'Media Base')}>{money(row.total_base_media_value)}</td>
                <td data-label={tableHeader('engagement_value', 'Engagement Value')}>{money(row.total_engagement_value)}</td>
                <td className={styles.goldText} data-label={tableHeader('emv_total', 'EMV')}>{money(row.total_emv)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section className={`${styles.section} ${styles.sectionCard}`}>
        <ReportSectionHeading kicker="Evidence" title="Content log" caption="Delivered content remains available as supporting detail without competing with the executive summary." count={`${compactCount(totalAssets)} pieces`} />
        <div className={styles.tableWrap}>
          <table aria-label="Content log table">
            <thead><tr>{['creator', 'type', 'reach', 'impressions', 'views', 'engagements', 'er', 'media_base', 'engagement_value', 'emv_total'].map((key) => <th key={key}>{tableHeader(key, key.replaceAll('_', ' '))}</th>)}</tr></thead>
            <tbody>{contentLog.map((row) => (
              <tr key={row.content_id}>
                <td data-label={tableHeader('creator', 'Creator')}><CreatorIdentity row={row} /></td>
                <td data-label={tableHeader('type', 'Type')}>{row.content_type || unavailable}</td>
                <td data-label={tableHeader('reach', 'Reach')}>{fmt(row.reach)}</td>
                <td data-label={tableHeader('impressions', 'Impressions')}>{fmt(row.impressions)}</td>
                <td data-label={tableHeader('views', 'Views')}>{fmt(row.video_views)}</td>
                <td data-label={tableHeader('engagements', 'Engagements')}>{fmt(row.engagement_total)}</td>
                <td data-label={tableHeader('er', 'ER')}>{pct(row.engagement_rate)}</td>
                <td data-label={tableHeader('media_base', 'Media Base')}>{money(row.base_media_value)}</td>
                <td data-label={tableHeader('engagement_value', 'Engagement Value')}>{money(row.engagement_value)}</td>
                <td className={styles.goldText} data-label={tableHeader('emv_total', 'EMV')}>{money(row.total_emv)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section className={styles.section}>
        <div className={`${styles.accordion} ${methodologyOpen ? styles.accordionOpen : ''}`}>
          <button className={styles.accordionTrigger} type="button" aria-expanded={methodologyOpen} onClick={() => setMethodologyOpen((open) => !open)}>
            <div><h2>Valuation methodology &amp; rate card</h2><p>Reference rates used to calculate media exposure and engagement value.</p></div>
            <ChevronDown className={styles.chevron} size={20} />
          </button>
          {methodologyOpen && <div className={styles.accordionContent}>
            <div className={styles.rateGrid}>
              {Object.entries(report.rate_card || {}).filter(([key]) => !['id', 'name', 'updated_at'].includes(key)).map(([key, value]) => (
                <div className={styles.rate} key={key}>
                  <InfoLabel explanation={{ title: rt(`rate_card.help.${key}.title`, rateCardLabels[key] || key), description: rt(`rate_card.help.${key}.description`, 'Reference value used for this metric.'), formula: rt(`rate_card.help.${key}.formula`, 'Applied to the corresponding action volume.'), source: rt('rate_card.help.source', 'This value may vary by collaboration and business.') }}>{rt(`rate_card.fields.${key}`, rateCardLabels[key] || key.replaceAll('_', ' '))}</InfoLabel>
                  <strong>{['impressions_cpm', 'reach_cpm', 'video_views_cpm'].includes(key) ? `${money(value)} / 1,000` : money(value)}</strong>
                </div>
              ))}
            </div>
            <p className={styles.methodNote}>EMV is an estimated media valuation metric. It should not be presented as revenue, attributable sales, or ROAS unless supported by separate conversion and sales tracking.</p>
          </div>}
        </div>
      </section>

      <footer className={styles.footer}><div><div className={styles.footerBrand}>FOTH</div><span>The future of collaborations is no longer informal. It is structured.</span></div><div>© 2026 FOTH · Private Curated Network<br />Creator analytics report</div></footer>
    </div>
  );
};

const CollaborationReport = () => {
  const { t } = useTranslation('collab');
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const rt = useCallback((key, fallback, options = {}) => (
    t(`report.${key}`, { defaultValue: fallback, ...options })
  ), [t]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCollabReport(id);
      setReport(response.data);
    } catch (error) {
      toast.error(rt('messages.load_error', 'No se pudo cargar el reporte.'));
    } finally {
      setLoading(false);
    }
  }, [id, rt]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    try {
      setRefreshing(true);
      const response = await generateCollabReport(id);
      setReport(response.data);
      toast.success(rt('messages.refresh_success', 'Reporte actualizado.'));
    } catch (error) {
      toast.error(rt('messages.refresh_error', 'No se pudo generar el reporte.'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      setDownloading(format);
      const response = await downloadCollabReport(id, format);
      const disposition = response.headers?.['content-disposition'];
      const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] || `foth-report.${format}`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error(rt('messages.download_error', 'No se pudo descargar el reporte.'));
    } finally {
      setDownloading(null);
    }
  };

  const dashboard = report?.dashboard || {};
  const dataStatus = report?.data_status;
  const freshness = dataStatus?.freshness;
  const charts = report?.charts || {};
  const creatorSummary = report?.creator_summary || [];
  const contentLog = report?.content_log || [];
  const isFinalizedSnapshot = dataStatus?.mode === 'snapshot'
    && ['finalized', 'historical'].includes(dataStatus?.analytics_state);
  const reportTimestamp = dataStatus?.mode === 'snapshot'
    ? dataStatus?.calculated_at || report?.report?.generated_at
    : dataStatus?.last_updated;
  const metricHelp = {
    emvTotal: {
      title: rt('explainers.emv_total.title', 'EMV total'),
      description: rt('explainers.emv_total.description', 'Valor estimado de la exposición y las interacciones generadas por el contenido de la colaboración. No representa ingresos atribuidos.'),
      formula: rt('explainers.emv_total.formula', 'EMV = Media base + Engagement value'),
      source: rt('explainers.common.source', 'Fuente: Insights certificados de Instagram/Meta + Rate Card de esta colaboración.'),
    },
    reach: {
      title: rt('explainers.reach.title', 'Alcance'),
      description: rt('explainers.reach.description', 'Cuentas únicas que vieron el contenido, según los Insights disponibles para cada pieza.'),
      formula: rt('explainers.reach.formula', 'Alcance total = suma del reach de cada pieza'),
      source: rt('explainers.common.source', 'Fuente: Insights certificados de Instagram/Meta + Rate Card de esta colaboración.'),
    },
    impressions: {
      title: rt('explainers.impressions.title', 'Impresiones'),
      description: rt('explainers.impressions.description', 'Veces totales que el contenido fue mostrado. Una misma cuenta puede generar más de una impresión.'),
      formula: rt('explainers.impressions.formula', 'Impresiones totales = suma de impresiones por pieza'),
      source: rt('explainers.common.source', 'Fuente: Insights certificados de Instagram/Meta + Rate Card de esta colaboración.'),
    },
    engagements: {
      title: rt('explainers.engagements.title', 'Engagements'),
      description: rt('explainers.engagements.description', 'Interacciones visibles que indican una respuesta activa al contenido.'),
      formula: rt('explainers.engagements.formula', 'Engagements = likes + comentarios + shares + saves'),
      source: rt('explainers.common.source', 'Fuente: Insights certificados de Instagram/Meta + Rate Card de esta colaboración.'),
    },
    engagementRate: {
      title: rt('explainers.engagement_rate.title', 'Engagement rate (ER)'),
      description: rt('explainers.engagement_rate.description', 'Mide qué proporción del alcance interactuó con el contenido. Para el total se calcula con los acumulados, no promediando porcentajes de piezas.'),
      formula: rt('explainers.engagement_rate.formula', 'ER = (Engagements / Alcance) × 100'),
      source: rt('explainers.common.source', 'Fuente: Insights certificados de Instagram/Meta + Rate Card de esta colaboración.'),
    },
    creators: {
      title: rt('explainers.creators.title', 'Creadores'),
      description: rt('explainers.creators.description', 'Número de creadores distintos que tienen al menos una pieza incluida en el reporte.'),
      formula: rt('explainers.creators.formula', 'Creadores = conteo de perfiles únicos con contenido'),
      source: rt('explainers.common.source', 'Fuente: entregables certificados de la colaboración.'),
    },
    assets: {
      title: rt('explainers.assets.title', 'Piezas entregadas'),
      description: rt('explainers.assets.description', 'Cantidad de publicaciones, reels o stories con datos incluidos en el reporte.'),
      formula: rt('explainers.assets.formula', 'Piezas = conteo de contenidos certificados'),
      source: rt('explainers.common.source', 'Fuente: entregables certificados de la colaboración.'),
    },
    views: {
      title: rt('explainers.views.title', 'Vistas'),
      description: rt('explainers.views.description', 'Reproducciones reportadas para video. Si Meta no entrega views, el sistema usa plays y luego impresiones como respaldo.'),
      formula: rt('explainers.views.formula', 'Vistas por pieza = views → plays → impressions'),
      source: rt('explainers.common.source', 'Fuente: Insights certificados de Instagram/Meta + Rate Card de esta colaboración.'),
    },
    mediaBase: {
      title: rt('explainers.media_base.title', 'Media base'),
      description: rt('explainers.media_base.description', 'Valor estimado de la distribución del contenido antes de valorar sus interacciones.'),
      formula: rt('explainers.media_base.formula', 'Media base = (Impresiones / 1.000 × CPM impresiones) + (Alcance / 1.000 × CPM reach) + (Vistas / 1.000 × CPM video)'),
      source: rt('explainers.common.source', 'Fuente: Insights certificados de Instagram/Meta + Rate Card de esta colaboración.'),
    },
    engagementValue: {
      title: rt('explainers.engagement_value.title', 'Engagement value'),
      description: rt('explainers.engagement_value.description', 'Valor estimado de las acciones que las personas realizaron sobre el contenido, usando el valor unitario del Rate Card.'),
      formula: rt('explainers.engagement_value.formula', 'EV = likes×valor + comentarios×valor + shares×valor + saves×valor + clicks×valor + visitas al perfil×valor'),
      source: rt('explainers.common.source', 'Fuente: Insights certificados de Instagram/Meta + Rate Card de esta colaboración.'),
    },
    plannedInvestment: {
      title: rt('explainers.planned_investment.title', 'Inversión declarada planificada'),
      description: rt('explainers.planned_investment.description', 'Valor declarado si se utilizan todos los cupos planificados de la colaboración.'),
      formula: rt('explainers.planned_investment.formula', 'Cupos planificados × valor declarado por creador'),
      source: rt('explainers.investment_source', 'Fuente: términos declarados en la colaboración.'),
    },
    realizedInvestment: {
      title: rt('explainers.realized_investment.title', 'Inversión declarada realizada'),
      description: rt('explainers.realized_investment.description', 'Suma del valor declarado para cada creador que completó la colaboración o entregó contenido después de ser aceptado.'),
      formula: rt('explainers.realized_investment.formula', 'Application completed OR accepted Application with at least one deliverable'),
      source: rt('explainers.investment_source', 'Fuente: Application de la colaboración y términos declarados.'),
    },
    estimatedMediaReturn: {
      title: rt('explainers.estimated_media_return.title', 'Estimated Media Return'),
      description: rt('explainers.estimated_media_return.description', 'Múltiplo estimado del valor de medios generado frente a la inversión declarada realizada. No representa ingresos atribuidos.'),
      formula: rt('explainers.estimated_media_return.formula', 'Estimated Media Return = EMV / Inversión declarada realizada'),
      source: rt('explainers.common.source', 'Fuente: EMV calculado + inversión declarada de participantes.'),
    },
  };

  if (loading) {
    return (
      <div className={styles.statePage}>
        <RefreshCw className={styles.spin} size={28} />
        <h1>{rt('states.loading_title', 'Cargando reporte')}</h1>
        <p>{rt('states.loading_body', 'Estamos preparando las metricas EMV de esta colaboracion.')}</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className={styles.statePage}>
        <Database size={30} />
        <h1>{rt('states.unavailable_title', 'Reporte no disponible')}</h1>
        <p>{rt('states.unavailable_body', 'No pudimos encontrar datos suficientes para esta colaboracion.')}</p>
      </div>
    );
  }

  return (
    <ReportRedesign
      report={report}
      rt={rt}
      dataStatus={dataStatus}
      freshness={freshness}
      reportTimestamp={reportTimestamp}
      isFinalizedSnapshot={isFinalizedSnapshot}
      dashboard={dashboard}
      creatorSummary={creatorSummary}
      contentLog={contentLog}
      charts={charts}
      metricHelp={metricHelp}
      refreshing={refreshing}
      downloading={downloading}
      handleGenerate={handleGenerate}
      handleDownload={handleDownload}
      onBack={() => navigate(`/business/collabs/${id}/summary`)}
    />
  );
};

export default CollaborationReport;
